import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Settings, User } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Toaster } from "@/components/ui/sonner";

function initials(name: string | null, handle: string) {
  const src = (name || handle || "?").trim();
  const parts = src.split(/\s+/).slice(0, 2);
  return parts.map(p => p[0]).join("").toUpperCase();
}

export function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isHome = loc.pathname === "/";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header
        className={
          isHome
            ? "absolute inset-x-0 top-0 z-20 border-b border-white/10 bg-transparent text-white"
            : "sticky top-0 z-20 border-b bg-background/80 backdrop-blur"
        }
      >
        <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">
          <Link to="/" className="flex items-center gap-2">
            <span
              className={
                isHome
                  ? "inline-block h-6 w-6 rounded-sm bg-white/90"
                  : "inline-block h-6 w-6 rounded-sm bg-foreground"
              }
              style={{
                backgroundImage:
                  "radial-gradient(circle at 30% 30%, currentColor 0 40%, transparent 42%)",
                color: isHome ? "rgba(255,255,255,0.9)" : "hsl(var(--foreground))",
              }}
            />
            <span className="text-sm font-semibold tracking-tight">fanfix</span>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              /OS
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            <NavItem to="/discover" label="Discover" home={isHome} />
            {user && <NavItem to="/dashboard" label="Dashboard" home={isHome} />}
            {user?.is_creator ? (
              <NavItem to="/studio" label="Studio" home={isHome} />
            ) : null}
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant={isHome ? "secondary" : "ghost"} size="sm" className="gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={user.avatar_url ?? undefined} />
                      <AvatarFallback className="text-[10px]">
                        {initials(user.display_name, user.handle)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline">{user.display_name || user.handle}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    @{user.handle}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => nav(`/${user.handle}`)}>
                    <User className="mr-2 h-4 w-4" /> My public page
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => nav("/dashboard")}>
                    Dashboard
                  </DropdownMenuItem>
                  {user.is_creator ? (
                    <DropdownMenuItem onSelect={() => nav("/studio")}>
                      Studio (creator)
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem onSelect={() => nav("/settings")}>
                    <Settings className="mr-2 h-4 w-4" /> Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={async () => {
                      await logout();
                      nav("/");
                    }}
                  >
                    <LogOut className="mr-2 h-4 w-4" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button asChild variant={isHome ? "secondary" : "ghost"} size="sm">
                  <Link to="/login">Sign in</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/signup">Get started</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className={isHome ? "" : "mx-auto max-w-6xl px-4 py-6 md:py-10"}>
        <Outlet />
      </main>

      <footer className="mt-20 border-t bg-background">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-8 text-xs text-muted-foreground md:flex-row">
          <span>fanfix.me — a commissions OS.</span>
          <nav className="flex flex-wrap items-center gap-5">
            <Link to="/discover" className="hover:text-foreground">Discover</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
            <Link to="/ai-policy" className="hover:text-foreground">AI policy</Link>
          </nav>
        </div>
      </footer>
      <Toaster position="bottom-right" />
    </div>
  );
}

function NavItem({ to, label, home }: { to: string; label: string; home: boolean }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "rounded-md px-3 py-1.5 text-sm transition",
          home
            ? isActive
              ? "bg-white/15 text-white"
              : "text-white/80 hover:bg-white/10 hover:text-white"
            : isActive
              ? "bg-accent text-accent-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-foreground",
        ].join(" ")
      }
    >
      {label}
    </NavLink>
  );
}
